import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "@tanstack/react-form"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import AdminPermissionMatrix from "@/components/AdminPermissionMatrix"
import CouncilMultiSelect from "@/components/council-multi-select"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { useToast } from "@/hooks/use-toast"
import { useCreateAdmin } from "@/hooks/api/use-get-admin-list"
import { councilAdminApi } from "@/lib/api"
import { AdminPermissionDTO } from "@/gen/api"
import {
  AdminPermissionSelection,
  AdminRolePreset,
  hasPermission,
  mergePermissionSelection,
  mergePermissionSelectionForCouncils,
  permissionSelectionForRoles,
} from "@/lib/admin-permissions"

interface CreateAdminFormData {
  name: string
  firstName: string
  lastName: string
  email: string
  password: string
  phoneNumber?: string
  role: "super_admin" | "admin" | "read_only" | "council_admin"
  rolePresets: AdminRolePreset[]
  councilId?: number
  councilIds?: number[]
  enforceTwoFactor?: boolean
  permissions?: AdminPermissionDTO[]
  permissionSelection: AdminPermissionSelection
}

export default function CreateAdminPage() {
  const { councilId } = useParams()
  const navigate = useNavigate()
  const scopedCouncilId = councilId ? Number(councilId) : undefined
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const { toast } = useToast()
  const createAdminMutation = useCreateAdmin()
  const backPath = scopedCouncilId ? `/council/${scopedCouncilId}/manage-admin` : "/manage-admin"
  const canCreateAdmin =
    hasRole("super_admin") ||
    hasPermission(user?.permissions, "admin_management", "write_all", scopedCouncilId)
  const defaultRolePresets: AdminRolePreset[] =
    hasRole("super_admin") && !scopedCouncilId ? ["super_admin"] : ["admin"]

  const form = useForm({
    defaultValues: {
      name: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phoneNumber: "",
      role: defaultRolePresets.includes("super_admin") ? "super_admin" : "admin",
      rolePresets: defaultRolePresets,
      councilId: scopedCouncilId ?? (hasRole("council_admin") ? user?.councilId : undefined),
      councilIds: scopedCouncilId
        ? [scopedCouncilId]
        : hasRole("council_admin") && user?.councilId
          ? [user.councilId]
          : [],
      enforceTwoFactor: false,
      permissionSelection: permissionSelectionForRoles(defaultRolePresets),
    } as CreateAdminFormData,
    onSubmit: async ({ value }) => {
      if (!canCreateAdmin) return
      const { permissionSelection, ...admin } = value
      const request = {
        ...admin,
        name: [value.firstName, value.lastName].filter(Boolean).join(" "),
        role: value.rolePresets.includes("super_admin") ? "super_admin" : "admin",
        councilId: scopedCouncilId ?? value.councilIds?.[0],
        councilIds: scopedCouncilId
          ? [scopedCouncilId]
          : !value.rolePresets.includes("super_admin")
            ? value.councilIds
            : [],
        permissions: scopedCouncilId
          ? mergePermissionSelectionForCouncils([], permissionSelection, [scopedCouncilId])
          : mergePermissionSelection([], permissionSelection),
      }

      if (scopedCouncilId) {
        await councilAdminApi.createCouncilAdminV2(scopedCouncilId, {
          ...request,
          role: "council_admin",
        } as any)
      } else {
        await createAdminMutation.mutateAsync({
          ...request,
          councilId: request.councilIds?.[0] ?? request.councilId,
        } as any)
      }
      toast({ title: "Admin Created", description: `${request.name} has been added as an admin.` })
      navigate(backPath)
    },
  })

  if (!canCreateAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader onBack={() => navigate(backPath)} />
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold">Access denied</h2>
          <p className="mt-2 text-muted-foreground">
            You need Admin Management Write All permission to create admins.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader onBack={() => navigate(backPath)} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-6"
      >
        <div className="rounded-lg border bg-card p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <form.Field
              name="firstName"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? "First name is required"
                    : value.length < 2
                      ? "First name must be at least 2 characters"
                      : undefined,
              }}
            >
              {(field) => (
                <TextField
                  id="admin-first-name"
                  label="First Name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]}
                  placeholder="Enter first name"
                />
              )}
            </form.Field>
            <form.Field name="lastName">
              {(field) => (
                <TextField
                  id="admin-last-name"
                  label="Last Name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  placeholder="Enter last name"
                />
              )}
            </form.Field>
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? "Email is required"
                    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                      ? "Invalid email format"
                      : undefined,
              }}
            >
              {(field) => (
                <TextField
                  id="admin-email"
                  label="Email Address"
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]}
                  placeholder="Enter email address"
                />
              )}
            </form.Field>
            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? "Password is required"
                    : value.length < 6
                      ? "Password must be at least 6 characters"
                      : undefined,
              }}
            >
              {(field) => (
                <TextField
                  id="admin-password"
                  label="Password"
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]}
                  placeholder="Enter password"
                />
              )}
            </form.Field>
            <form.Field name="phoneNumber">
              {(field) => (
                <TextField
                  id="admin-phone"
                  label="Phone Number"
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  placeholder="Enter phone number"
                />
              )}
            </form.Field>
            <form.Field name="enforceTwoFactor">
              {(field) => (
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label>Enforce 2FA</Label>
                    <p className="text-sm text-muted-foreground">Require setup on next login.</p>
                  </div>
                  <Switch
                    checked={Boolean(field.state.value)}
                    onCheckedChange={(checked) => field.handleChange(checked)}
                  />
                </div>
              )}
            </form.Field>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-4">
          {!scopedCouncilId ? (
            <>
              <form.Field name="rolePresets">
                {(field) => (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Roles</div>
                    <div className="grid gap-2 rounded-md border p-3">
                      {(["super_admin", "admin", "read_only"] as AdminRolePreset[]).map((role) => (
                        <div key={role} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={field.state.value.includes(role)}
                            disabled={role === "super_admin" && !hasRole("super_admin")}
                            onCheckedChange={(checked) => {
                              const nextRoles = checked
                                ? Array.from(new Set([...field.state.value, role]))
                                : field.state.value.filter((value) => value !== role)
                              const safeRoles: AdminRolePreset[] = nextRoles.length
                                ? nextRoles
                                : ["read_only"]
                              field.handleChange(safeRoles)
                              form.setFieldValue(
                                "role",
                                safeRoles.includes("super_admin") ? "super_admin" : "admin",
                              )
                              form.setFieldValue(
                                "permissionSelection",
                                permissionSelectionForRoles(safeRoles),
                              )
                            }}
                          />
                          <span>
                            {role === "super_admin"
                              ? "N3 Admin"
                              : role === "admin"
                                ? "Admin"
                                : "Read Only"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </form.Field>
              <form.Subscribe selector={(state) => state.values.rolePresets}>
                {(roles) =>
                  !roles.includes("super_admin") && (
                    <form.Field
                      name="councilIds"
                      validators={{
                        onChange: ({ value }) =>
                          !value?.length ? "At least one council is required" : undefined,
                      }}
                    >
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Council Access</Label>
                          <CouncilMultiSelect
                            value={field.state.value ?? []}
                            onChange={field.handleChange}
                          />
                          {field.state.meta.errors.length > 0 && (
                            <p className="text-sm text-red-500">{field.state.meta.errors[0]}</p>
                          )}
                        </div>
                      )}
                    </form.Field>
                  )
                }
              </form.Subscribe>
            </>
          ) : null}
        </div>

        <form.Field name="permissionSelection">
          {(field) => (
            <div className="space-y-3 rounded-lg border bg-card p-6">
              <div>
                <Label>Permissions</Label>
                <p className="text-sm text-muted-foreground">
                  Choose read/write scope for each service in selected council access.
                </p>
              </div>
              <AdminPermissionMatrix
                idPrefix="create-admin-page-permission"
                value={field.state.value}
                onChange={field.handleChange}
              />
            </div>
          )}
        </form.Field>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(backPath)}
            disabled={form.state.isSubmitting}
          >
            Cancel
          </Button>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canCreateAdmin || !canSubmit || Boolean(isSubmitting)}
              >
                {isSubmitting ? "Creating..." : "Create Admin"}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  )
}

function PageHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center gap-4">
      <Button variant="outline" size="sm" onClick={onBack} className="flex items-center gap-2">
        <ArrowLeft className="size-4" />
        Back
      </Button>
      <div>
        <h1 className="text-3xl font-bold">Create New Admin</h1>
        <p className="text-muted-foreground">
          Add an admin profile, assign council access, and configure permissions.
        </p>
      </div>
    </div>
  )
}

function TextField({
  id,
  label,
  value,
  onBlur,
  onChange,
  error,
  placeholder,
  type = "text",
}: {
  id: string
  label: string
  value: string
  onBlur: () => void
  onChange: (value: string) => void
  error?: string
  placeholder?: string
  type?: string
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
